// ─── LEGAL PAGES ─────────────────────────────────────────────
// Drop this file into src/pages/LegalPages.jsx
// All 5 legal pages: Terms, Risk, Privacy, Refund, KYC/AML

const LegalLayout = ({ title, subtitle, children, onBack }) => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
    {/* Nav */}
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 60px',
      background: 'rgba(8,10,15,0.9)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid var(--border)',
    }}>
      <div
        style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, cursor: 'pointer' }}
        onClick={onBack}
      >
        <span style={{ color: 'var(--green)' }}>Clear</span>Fund
      </div>
      <button
        onClick={onBack}
        style={{
          background: 'transparent', border: '1px solid var(--border)',
          color: 'var(--text2)', borderRadius: 'var(--radius)',
          padding: '7px 18px', cursor: 'pointer', fontSize: 13,
          fontFamily: 'var(--font-body)',
        }}
      >
        ← Back to Home
      </button>
    </div>

    {/* Header */}
    <div style={{
      paddingTop: 120, paddingBottom: 48,
      paddingLeft: 60, paddingRight: 60,
      maxWidth: 860, margin: '0 auto',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'var(--green-dim)', border: '1px solid rgba(0,229,160,0.2)',
        borderRadius: 100, padding: '4px 14px', marginBottom: 20,
        fontSize: 11, color: 'var(--green)', fontWeight: 600,
        fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
      }}>
        CLEARFUND TRADING LLC · LEGAL
      </div>
      <h1 style={{
        fontFamily: 'var(--font-display)', fontWeight: 800,
        fontSize: 42, letterSpacing: '-0.03em', marginBottom: 12,
      }}>{title}</h1>
      <p style={{ fontSize: 14, color: 'var(--text3)' }}>{subtitle} · Effective March 2, 2026</p>
    </div>

    {/* Content */}
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 60px 80px' }}>
      {children}
    </div>

    {/* Footer */}
    <div style={{
      borderTop: '1px solid var(--border)', padding: '28px 60px',
      maxWidth: 860, margin: '0 auto',
      fontSize: 12, color: 'var(--text3)', lineHeight: 1.7,
    }}>
      Questions? Contact us at{' '}
      <span style={{ color: 'var(--green)' }}>monyrangsey@clearfundtrading.com</span>
      <br />© 2026 ClearFund Trading LLC. All rights reserved.
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{
      fontFamily: 'var(--font-display)', fontWeight: 700,
      fontSize: 18, marginBottom: 14,
      paddingBottom: 10, borderBottom: '1px solid var(--border)',
      color: 'var(--text)',
    }}>{title}</h2>
    <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.85 }}>
      {children}
    </div>
  </div>
);

const Ul = ({ items }) => (
  <ul style={{ paddingLeft: 20, margin: '10px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
    {items.map((item, i) => (
      <li key={i} style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7 }}>{item}</li>
    ))}
  </ul>
);

const Callout = ({ color = 'var(--amber)', children }) => (
  <div style={{
    background: `${color}18`, border: `1px solid ${color}33`,
    borderRadius: 'var(--radius)', padding: '14px 18px',
    fontSize: 13, color: 'var(--text)', lineHeight: 1.7, margin: '16px 0',
  }}>
    {children}
  </div>
);

const Table = ({ headers, rows }) => (
  <div style={{ overflowX: 'auto', margin: '16px 0' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{
              background: 'var(--surface2)', color: 'var(--text2)',
              padding: '10px 14px', textAlign: 'left',
              border: '1px solid var(--border)',
              fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} style={{
                padding: '10px 14px', border: '1px solid var(--border)',
                background: i % 2 === 0 ? 'var(--surface)' : 'transparent',
                color: 'var(--text2)',
              }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── 1. TERMS OF SERVICE ──────────────────────────────────────
export const TermsPage = ({ onBack }) => (
  <LegalLayout title="Terms of Service" subtitle="ClearFund Trading LLC" onBack={onBack}>

    <Section title="1. Acceptance of Terms">
      <p>By accessing or using the ClearFund Trading platform ("Platform"), you agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and ClearFund Trading LLC, a California limited liability company. If you do not agree, you may not use the Platform.</p>
    </Section>

    <Section title="2. Eligibility">
      <p>You must be at least 18 years of age to participate. By registering, you represent that you meet this requirement and that your participation does not violate any applicable laws in your jurisdiction. Residents of sanctioned jurisdictions are not eligible.</p>
    </Section>

    <Section title="3. Nature of the Platform">
      <p>ClearFund Trading operates a performance-based proprietary trading evaluation platform. All trading is conducted in a simulated environment. No real capital is deployed by users during evaluations. ClearFund does not trade against clients, does not provide investment advice, and does not act as a broker, dealer, or clearing firm.</p>
      <Callout color="var(--green)">
        <strong>Our Core Commitments:</strong> ClearFund does not retroactively modify account rules. ClearFund does not deny or delay payouts for liquidity management purposes. All rules are published, transparent, and enforced uniformly. Rules applicable to an account at creation are locked for the life of that account.
      </Callout>
    </Section>

    <Section title="4. Account Types & Evaluation Pricing">
      <p>ClearFund offers two account types. Evaluation fees are one-time charges. No subscriptions, recurring fees, or time limits apply.</p>
      <Table
        headers={['Account Type', '$50,000', '$100,000', '$150,000']}
        rows={[
          ['Core', '$99', '$139', '$179'],
          ['Velocity', '$199', '$269', '$319'],
        ]}
      />
    </Section>

    <Section title="5. Evaluation Rules">
      <p>All evaluation accounts are subject to the following rules. Violation of any rule results in immediate evaluation termination:</p>
      <Ul items={[
        'Profit Target: must be met without triggering any risk rule',
        'Maximum Drawdown: trailing drawdown calculated on realized equity; breach fails evaluation immediately',
        'Daily Loss Limit: losses exceeding the daily limit fail the evaluation; resets at session reset time',
        'Consistency Rule (Evaluation Only): no single trading day may account for more than 50% of total evaluation profits',
        'Minimum Trading Days: a minimum number of qualifying trading days is required',
        'No Time Limit: evaluations do not expire and no recurring fees apply',
      ]} />
    </Section>

    <Section title="6. Funded Account Rules">
      <p>Upon passing an evaluation, accounts transition to funded status under a simplified ruleset:</p>
      <Ul items={[
        'No consistency rule applies to funded accounts',
        'Ongoing risk limits: maximum drawdown and defined risk compliance monitoring',
        'No discretionary rule changes mid-account',
        'No retroactive enforcement',
        'Post-payout balance-to-zero failure: if the account balance reaches $0 USD at any time after a payout, the account is permanently closed',
      ]} />
    </Section>

    <Section title="7. Profit Split">
      <p>Funded account holders receive the following profit split:</p>
      <Ul items={[
        'Payouts 1 through 3: trader retains 100% of profits',
        'Payout 4 and beyond: trader retains 90%, ClearFund retains 10%',
      ]} />
      <p style={{ marginTop: 10 }}>This applies to both Core and Velocity accounts.</p>
    </Section>

    <Section title="8. Payout Structure">
      <p>All payouts are calculated as <strong>50% of the account balance</strong> at the time of payout review, subject to the applicable payout cap. The approved payout shall be the lesser of: (a) 50% of account balance, or (b) the applicable cap.</p>
      <p style={{ marginTop: 10 }}>A <strong>Winning Day</strong> is defined as any trading day in which the trader realizes a net closed profit of at least <strong>$150 USD</strong>. Only Winning Days count toward payout eligibility.</p>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--text)' }}>Core Account — Payout Cap</p>
      <p>Maximum payout per cycle: <strong>$4,000</strong> (applies indefinitely to all payouts).</p>

      <p style={{ marginTop: 16, fontWeight: 600, color: 'var(--text)' }}>Velocity Account — Payout Caps</p>
      <Table
        headers={['Payout Count', 'Maximum Payout']}
        rows={[
          ['1 – 2', '$3,000'],
          ['3 – 5', '$6,000'],
          ['6+', 'No cap'],
        ]}
      />
      <p>Payout processing window: 24–48 hours. ClearFund does not delay payouts to manage cash flow.</p>
    </Section>

    <Section title="9. Payout Farming Prohibition">
      <p>ClearFund does not permit payout farming. If a trader generates a disproportionate share of total profits from a single trading day and subsequently reduces activity to minimal levels for the purpose of reaching a higher payout stage, ClearFund reserves the right to limit the payout, defer for further review, or require additional qualifying Winning Days prior to approval.</p>
    </Section>

    <Section title="10. Prohibited Conduct">
      <p>The following result in immediate account termination and permanent ban:</p>
      <Ul items={[
        'Account sharing or third-party account management',
        'Trade copying or mirroring across multiple accounts',
        'Hedging strategies across related ClearFund accounts',
        'Latency exploitation, data errors, or pricing discrepancy abuse',
        'Automated systems designed to circumvent platform rules',
        'Any form of market manipulation or deceptive trading',
        'Creating multiple accounts without written authorization',
      ]} />
      <p style={{ marginTop: 10 }}>Only explicitly listed violations are enforceable. ClearFund will not apply new violation categories retroactively.</p>
    </Section>

    <Section title="11. Rule Immutability">
      <Callout color="var(--green)">
        Rules applicable to an account at the time of creation are <strong>locked for the life of that account</strong>. Any amendments to the Program Rules apply only to newly created accounts. ClearFund will never change rules mid-account for any reason, including liquidity management.
      </Callout>
    </Section>

    <Section title="12. Limitation of Liability">
      <p>ClearFund Trading LLC is not liable for indirect, incidental, special, consequential, or punitive damages. Our total liability shall not exceed the amount paid by you for the specific evaluation giving rise to the claim.</p>
    </Section>

    <Section title="13. Governing Law">
      <p>These Terms are governed by the laws of the State of California. Disputes shall be resolved in state or federal courts in California.</p>
    </Section>

  </LegalLayout>
);

// ─── 2. RISK DISCLOSURE ───────────────────────────────────────
export const RiskPage = ({ onBack }) => (
  <LegalLayout title="Risk Disclosure" subtitle="ClearFund Trading LLC" onBack={onBack}>

    <Callout color="var(--red)">
      <strong>WARNING:</strong> TRADING FUTURES AND LEVERAGED INSTRUMENTS INVOLVES SUBSTANTIAL RISK OF LOSS. MOST TRADERS DO NOT ACHIEVE CONSISTENT PROFITABILITY. THIS PLATFORM OPERATES IN A SIMULATED ENVIRONMENT. PAST PERFORMANCE IS NOT INDICATIVE OF FUTURE RESULTS.
    </Callout>

    <Section title="1. Simulated Environment">
      <p>All trading on the Platform is simulated. Prices, fills, and conditions may not reflect live market conditions. Simulated results may differ materially from real trading outcomes due to liquidity, slippage, execution speed, and psychological factors.</p>
    </Section>

    <Section title="2. Evaluation Risk">
      <p>A majority of evaluation participants do not pass. Evaluation fees are non-refundable once credentials are provisioned. There is no guarantee of passing an evaluation or receiving a funded account.</p>
      <Ul items={[
        'The 50% consistency rule means no evaluation can be passed in a single trading day',
        'A minimum number of Winning Days (net closed profit ≥ $150) are required',
        'Any rule violation results in immediate evaluation failure with no exceptions',
      ]} />
    </Section>

    <Section title="3. Funded Account Risk">
      <p>Funded accounts remain subject to drawdown and risk compliance rules. Breach of maximum drawdown results in immediate account closure.</p>
      <Ul items={[
        'After any payout, if the account balance reaches $0, the account is permanently closed',
        'Payouts are subject to compliance review and KYC verification',
        'Payout farming activity may result in payout deferral or reduction',
      ]} />
    </Section>

    <Section title="4. Payout Risk">
      <p>Payouts are capped per the applicable payout structure:</p>
      <Ul items={[
        'Core accounts: permanently capped at $4,000 per cycle',
        'Velocity accounts: $3,000 cap (payouts 1–2), $6,000 cap (payouts 3–5), no cap (payout 6+)',
        'All payouts are calculated as 50% of account balance at time of review',
        'ClearFund participates in a 10% profit split beginning with the fourth payout',
      ]} />
    </Section>

    <Section title="5. Technology Risk">
      <p>Platform availability is not guaranteed. Technical failures or connectivity issues may affect trading. We are not liable for losses resulting from downtime or technical errors.</p>
    </Section>

    <Section title="6. No Investment Advice">
      <p>ClearFund Trading LLC does not provide investment, financial, or trading advice. Nothing on the Platform constitutes a recommendation. You are solely responsible for all trading decisions made during your evaluation or funded account.</p>
    </Section>

    <Section title="7. Acknowledgment">
      <p>By participating in ClearFund's program, you confirm that you have read and understood all risks described herein, that you are participating voluntarily with full awareness of these risks, and that you are financially able to sustain the loss of any evaluation fees paid.</p>
    </Section>

  </LegalLayout>
);

// ─── 3. PRIVACY POLICY ───────────────────────────────────────
export const PrivacyPage = ({ onBack }) => (
  <LegalLayout title="Privacy Policy" subtitle="ClearFund Trading LLC" onBack={onBack}>

    <Section title="1. Introduction">
      <p>ClearFund Trading LLC is committed to protecting your privacy. This Policy explains how we collect, use, share, and protect your information when you use the Platform at clearfundtrading.com.</p>
    </Section>

    <Section title="2. Information We Collect">
      <Ul items={[
        'Identity: full name, date of birth, government-issued ID (required for KYC)',
        'Contact: email address, phone number, mailing address',
        'Financial: payment details processed via Stripe; payout method details',
        'Trading: trade history, account balances, performance data, Winning Days',
        'Technical: IP address, device type, browser, session and login timestamps',
        'Communications: support correspondence and ticket submissions',
      ]} />
    </Section>

    <Section title="3. How We Use Your Information">
      <Ul items={[
        'Operate and maintain the Platform',
        'Process evaluation payments and funded account payouts',
        'Enforce Program Rules, including payout farming detection and multi-account monitoring',
        'Verify identity for KYC/AML compliance before processing payouts',
        'Detect fraud, abuse, and unauthorized activity',
        'Send transactional emails (purchase confirmation, payout status, rule breach alerts)',
        'Comply with legal obligations and regulatory requirements',
      ]} />
    </Section>

    <Section title="4. Data Sharing">
      <p>We do not sell your personal information. We share data only with:</p>
      <Ul items={[
        'Stripe — payment processing',
        'KYC vendor (Veriff / Sumsub) — identity verification',
        'Resend — transactional email delivery',
        'Law enforcement — when required by applicable law or court order',
        'Successors — in the event of merger, acquisition, or asset sale',
      ]} />
    </Section>

    <Section title="5. Data Retention">
      <p>Account data is retained while your account is active and for a reasonable period thereafter. Financial and KYC records are retained for a minimum of <strong>7 years</strong> for compliance purposes. You may request deletion of personal data subject to legal retention requirements by emailing monyrangsey@clearfundtrading.com.</p>
    </Section>

    <Section title="6. Your Rights">
      <p>Depending on your jurisdiction, you may have rights to access, correct, delete, or port your personal data. To exercise these rights, contact us at monyrangsey@clearfundtrading.com. We will respond within 30 days.</p>
    </Section>

    <Section title="7. Security">
      <p>We use industry-standard encryption, access controls, and regular security audits to protect your data. Payment data is handled exclusively by Stripe and is never stored on our servers. No system is completely secure, and we cannot guarantee absolute data security.</p>
    </Section>

    <Section title="8. Cookies">
      <p>We use essential cookies for authentication and session management only. We do not use advertising, tracking, or third-party analytics cookies. You may disable cookies in your browser settings, though this may affect platform functionality.</p>
    </Section>

    <Section title="9. Children's Privacy">
      <p>The Platform is not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors. If you believe a minor has provided us with data, please contact us immediately.</p>
    </Section>

  </LegalLayout>
);

// ─── 4. REFUND POLICY ────────────────────────────────────────
export const RefundPage = ({ onBack }) => (
  <LegalLayout title="Refund Policy" subtitle="ClearFund Trading LLC" onBack={onBack}>

    <Callout color="var(--red)">
      <strong>ALL SALES ARE FINAL.</strong> Evaluation fees are non-refundable once account credentials have been provisioned. By completing your purchase, you acknowledge and agree to this policy.
    </Callout>

    <Section title="1. No Refund After Provisioning">
      <p>This policy applies to all evaluation accounts (Core and Velocity, all sizes) and all reset purchases. Once your account credentials are delivered to your registered email, the service has been fully rendered and no refund will be issued.</p>
    </Section>

    <Section title="2. Rationale">
      <p>Evaluation accounts provide immediate access to a live simulated trading environment. Evaluation fees are one-time charges with no subscription component. This policy is consistent with industry standards for proprietary trading evaluation programs.</p>
    </Section>

    <Section title="3. Reset Purchases">
      <p>Failed evaluations may be reset for a fixed, disclosed fee. Reset fees are subject to the same no-refund policy. A reset restores the account to its initial balance and original ruleset as of the reset date.</p>
    </Section>

    <Section title="4. Limited Exceptions">
      <p>Refunds are considered only in the following circumstances, submitted within <strong>7 days</strong> of purchase:</p>
      <Ul items={[
        'Duplicate charge — charged twice for the exact same order',
        'Provisioning failure — account was never delivered due to a verified platform error',
        'Unauthorized transaction — documented evidence of unauthorized use of your payment method',
      ]} />
      <p style={{ marginTop: 10 }}>To submit a refund exception, email monyrangsey@clearfundtrading.com with your order details and supporting documentation.</p>
    </Section>

    <Section title="5. Chargebacks">
      <Callout color="var(--red)">
        Filing a chargeback without first contacting us is a violation of these Terms. Accounts associated with chargebacks are immediately suspended. We reserve the right to dispute all chargebacks and provide complete transaction evidence to payment processors. Repeated chargeback activity may result in a permanent ban.
      </Callout>
    </Section>

  </LegalLayout>
);

// ─── 5. KYC / AML POLICY ────────────────────────────────────
export const KYCAMLPage = ({ onBack }) => (
  <LegalLayout title="KYC / AML Policy" subtitle="ClearFund Trading LLC" onBack={onBack}>

    <Section title="1. Purpose">
      <p>ClearFund Trading LLC is committed to preventing money laundering, terrorist financing, and financial crimes. This Policy describes our procedures for identity verification and suspicious activity monitoring in compliance with applicable regulations.</p>
    </Section>

    <Section title="2. KYC Requirements">
      <p>KYC verification is <strong>mandatory before any payout is processed</strong>. Required documentation:</p>
      <Ul items={[
        'Government-issued photo ID (passport, driver\'s license, or national ID)',
        'Proof of address (utility bill or bank statement dated within 90 days)',
        'Selfie or liveness check confirming document ownership',
        'Date of birth confirmation — must be 18 years of age or older',
      ]} />
    </Section>

    <Section title="3. Payout Gate">
      <Callout color="var(--amber)">
        No payout will be processed until KYC status is <strong>approved</strong>. Submitted or pending KYC does not qualify. This requirement applies to all users and all payout amounts without exception.
      </Callout>
    </Section>

    <Section title="4. Verification Process">
      <p>Verification is conducted via our third-party KYC vendor. The process typically takes 5–15 minutes to complete. Review decisions are made within 1–24 hours. Additional documentation may be requested at any time during or after the review process.</p>
    </Section>

    <Section title="5. AML Monitoring">
      <p>Accounts are monitored on an ongoing basis for indicators including:</p>
      <Ul items={[
        'Profit patterns inconsistent with stated trading strategy',
        'Rapid or repeated payout requests following initial funding (payout farming)',
        'Multiple accounts from the same individual, household, or device',
        'Accounts associated with high-risk or sanctioned jurisdictions',
        'Activity patterns consistent with structuring or layering',
        'Single-day profit concentration followed by minimal subsequent activity',
      ]} />
    </Section>

    <Section title="6. Suspicious Activity">
      <p>Upon detection of suspicious activity, ClearFund may suspend the account, withhold payouts pending investigation, request additional documentation, and report to relevant authorities as required by law. ClearFund is not obligated to disclose the reason for account restrictions during an active investigation.</p>
    </Section>

    <Section title="7. Prohibited Jurisdictions">
      <p>We do not accept users from jurisdictions subject to comprehensive U.S. sanctions, including but not limited to North Korea, Iran, Cuba, Syria, and the Crimea region. This list is subject to change based on applicable OFAC and international sanctions law.</p>
    </Section>

    <Section title="8. Data Retention">
      <p>KYC records are retained for a minimum of <strong>5 years</strong> following account closure as required by applicable AML regulations.</p>
    </Section>

  </LegalLayout>
);
